import styles from './downloadCreditAccountStatusPDFModal.module.css'
import * as XLSX from 'xlsx-js-style'
import { useEffect, useState } from 'react'
import { usePDFActions } from "../../../../../features/files"
import { BooleanField, Modal, ModalId } from "../../../../../features/ui"
import { CreditAccountStatusPDF } from './creditAccountStatusPDF'
import { Data2, useDataTypesHooks } from '../../../../../features/data-types'
import { Customer } from '../../../../sales'
import { useSystemState } from '../../../../system'
import { firstItem } from '../../../../../utils/listUtils'

type SummaryValues = {
    limit: number
    available: number
    debt: number
    creditBalance?: number
    netBalance?: number
    netBalanceLabel: string
}

type Props = {
    customerId: string
    customer?: Customer
    customerName: string
    rows: Data2[]
    summary: {
        limit: string
        available: string
        debt: string
        creditBalance?: string
        netBalance?: string
        netBalanceLabel: string
    }
    summaryValues: SummaryValues
    dateFrom?: string | Date
    dateTo?: string | Date
}

const delay = (s: number) => new Promise(resolve => setTimeout(resolve, s * 1000))

const HEADER_FILL = '6D9891'
const CURRENCY_FORMAT = '"$" #,##0.00'

export const DownloadCreditAccountStatusPDFModal = (props: Props) => {
    const { customerId, customer, customerName, rows, summary, summaryValues, dateFrom, dateTo } = props

    const [includePDF, setIncludePDF] = useState(false)
    const [includeExcel, setIncludeExcel] = useState(false)

    const { generatePDF } = usePDFActions()
    const { formatDate, formatIdentifier, formatPhone } = useDataTypesHooks()

    // La fecha se toma al descargar para saber cuándo se generó cada estado de cuenta.
    const getFileName = (extension: string) =>
        `Estado_Cuenta_${customerName.replace(/\s+/g, '_')}_${formatDate(new Date(), '-')}.${extension}`
    const { company } = useSystemState()

    const pdfId = 'credit-account-status-pdf'

    useEffect(() => {
        setIncludePDF(false)
        setIncludeExcel(false)
    }, [customerId])

    const getFilterLabel = () => {
        const from = dateFrom ? formatDate(new Date(dateFrom)) : null
        const to = dateTo ? formatDate(new Date(dateTo)) : null

        if (from && to) return `Movimientos de Cuenta del ${from} al ${to}:`
        if (from) return `Movimientos de Cuenta desde el ${from}:`
        if (to) return `Movimientos de Cuenta hasta el ${to}:`
        return 'Movimientos de Cuenta:'
    }

    const downloadExcel = () => {
        const address = customer?.mainAddress?.address || '-'
        const identifier = customer ? formatIdentifier(customer.identifier) : '-'
        const phone = customer && customer.phones.length > 0 ? formatPhone(firstItem(customer.phones)) : '-'

        // Líneas informativas: el crédito a favor si lo hay, y el saldo solo si hay deuda y crédito a la vez
        const creditBalanceRows: (string | number)[][] = [
            ...(summaryValues.creditBalance !== undefined ? [['', '', '', 'Crédito a Favor:', summaryValues.creditBalance]] : []),
            ...(summaryValues.netBalance !== undefined ? [['', '', '', `${summaryValues.netBalanceLabel}:`, summaryValues.netBalance]] : [])
        ]

        const headerRows: (string | number)[][] = [
            [company!.name],
            ['Estado de Cuenta Corriente'],
            [],
            ['Cliente:', customerName, '', 'Fecha:', formatDate(new Date())],
            ['Dirección:', address, '', 'Crédito Otorgado:', summaryValues.limit],
            ['DNI/CUIT:', identifier, '', 'Crédito Disponible:', summaryValues.available],
            ['Teléfono:', phone, '', 'Deuda Total:', summaryValues.debt],
            ...creditBalanceRows,
            [],
            [getFilterLabel()],
            [],
            ['Fecha', 'Operación', 'Debe', 'Haber']
        ]
        const lastSummaryRowIndex = 6 + creditBalanceRows.length
        const filterLabelRowIndex = lastSummaryRowIndex + 2
        const tableHeaderRowIndex = headerRows.length - 1

        const dataRows = rows.map(row => [
            row.date ? formatDate(new Date(row.date as string)) : '-',
            row.operationLabel as string,
            row.debitValue as number | undefined,
            row.creditValue as number | undefined
        ])

        const worksheet = XLSX.utils.aoa_to_sheet([...headerRows, ...dataRows])

        const setCellStyle = (rowIndex: number, colIndex: number, style: object) => {
            const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })
            if (worksheet[cellRef]) worksheet[cellRef].s = style
        }
        const setCellCurrencyFormat = (rowIndex: number, colIndex: number) => {
            const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })
            if (worksheet[cellRef] && typeof worksheet[cellRef].v === 'number') {
                worksheet[cellRef].z = CURRENCY_FORMAT
                setCellStyle(rowIndex, colIndex, { alignment: { horizontal: 'right' } })
            }
        }

        setCellStyle(0, 0, { font: { bold: true, sz: 16 } })
        setCellStyle(1, 0, { font: { bold: true, sz: 13 } })
        for (let row = 3; row <= lastSummaryRowIndex; row++) {
            setCellStyle(row, 0, { font: { bold: true } })
            setCellStyle(row, 3, { font: { bold: true } })
        }
        for (let row = 4; row <= lastSummaryRowIndex; row++) {
            setCellCurrencyFormat(row, 4)
        }
        setCellStyle(filterLabelRowIndex, 0, { font: { italic: true } })

        for (let col = 0; col <= 3; col++) {
            setCellStyle(tableHeaderRowIndex, col, {
                font: { bold: true, color: { rgb: 'FFFFFF' } },
                fill: { fgColor: { rgb: HEADER_FILL } },
                alignment: { horizontal: 'center' }
            })
        }
        dataRows.forEach((_, index) => {
            const rowIndex = tableHeaderRowIndex + 1 + index
            setCellCurrencyFormat(rowIndex, 2)
            setCellCurrencyFormat(rowIndex, 3)
        })

        worksheet['!cols'] = [{ wch: 16 }, { wch: 32 }, { wch: 18 }, { wch: 22 }, { wch: 18 }]

        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Estado de Cuenta')
        XLSX.writeFile(workbook, getFileName('xlsx'))
    }

    const onSubmit = async () => {
        if (includePDF) {
            generatePDF(pdfId, getFileName('pdf'))
        }
        if (includePDF && includeExcel) {
            await delay(0.5)
        }
        if (includeExcel) {
            downloadExcel()
        }
    }

    const renderHiddenPDF = () => (
        <div className={styles.hidden}>
            <CreditAccountStatusPDF
                id={pdfId}
                customerId={customerId}
                rows={rows}
                summary={summary}
                dateFrom={dateFrom}
                dateTo={dateTo}
            />
        </div>
    )

    return (
        <Modal
            modalId={ModalId.DOWNLOAD_CREDIT_ACCOUNT_STATUS_PDF}
            title="Descargar Estado de Cuenta Corriente"
            submitLabel={(includePDF && includeExcel) ? 'Descargar Ambos' : 'Descargar'}
            submitIcon="download"
            disableSubmit={!includePDF && !includeExcel}
            onSubmit={onSubmit}
        >
            <div className={styles.container}>
                <p>Seleccione los formatos que desea descargar:</p>
                <BooleanField
                    id="check-pdf-credit-account-status"
                    label="Documento PDF (.pdf)"
                    value={includePDF}
                    onChange={value => setIncludePDF(!!value)}
                />
                <BooleanField
                    id="check-excel-credit-account-status"
                    label="Planilla Excel (.xlsx)"
                    value={includeExcel}
                    onChange={value => setIncludeExcel(!!value)}
                />
            </div>
            {renderHiddenPDF()}
        </Modal>
    )
}
